import { UserType } from "../../utils/Constants";

import { getPurchaserInfo } from "../../../specific/services/InAppPurchase/InAppPurchaseService";
import { version, build } from "../../../../package.json";
import { addPremiumTagToUser } from "../../services/Mailing";
import { AppTarget, getAppTarget } from "../../../specific/utils/Navigation/Host";
import { getNumericDateForAnalyticForTimestamp } from "../../utils/DateUtils";
import { addDataToUser } from "../../services/Firebase/Database";
import { AsyncStorageKey, getItemFromAsyncStorage, removeItemFromAsyncStorage, setItemToAsyncStorage } from "../../utils/AsyncStorage/AsyncStorage";

export type SubStatus = "free" | "premium" | "pre-sub" | "premium_trial" | "premium_tne";

class Code {
    public code: string;
    public key: string;

    constructor(code: { code: string, key: string }) {
        this.code = code.code;
        this.key = code.key;
    }
}

class Subscription {
    public code?: string;
    public creationDate?: number;
    public customer_email: string;
    public date: number;
    public expires_date: number;
    public pre_expires_date?: number;
    public origin: string;
    public product_id: string;
    public start_date: number;
    public auto_renewing: boolean;
    public period_type?: string;
    public stripe_sub_id?: string;
    public card_id?: string;

    constructor(subscription: { code?: string, card_id?:string, creationDate?: number, customer_email: string, date: number, expires_date: number, origin: string, product_id: string, start_date: number, auto_renewing: boolean, period_type?: string, stripe_sub_id?: string, pre_expires_date?: number }) {
        if (subscription !== undefined) {
            this.code = subscription.code !== undefined ? subscription.code : "";
            this.card_id = subscription.card_id !== undefined ? subscription.card_id : "";
            this.creationDate = subscription.creationDate;
            this.customer_email = subscription.customer_email;
            this.date = subscription.date;
            this.expires_date = subscription.expires_date;
            this.origin = subscription.origin;
            this.product_id = subscription.product_id;
            this.start_date = subscription.start_date;
            this.auto_renewing = subscription.auto_renewing;
            this.period_type = subscription.period_type ?? "NORMAL";
            this.stripe_sub_id = subscription.stripe_sub_id;
            this.pre_expires_date = subscription.pre_expires_date ?? 0;
        }
    }
}

type Steps = {
    filter?: boolean,
    tutorial?: boolean,
    webinar?: boolean,
    advicesByLevel?: boolean,
    advicesByActivityType?: boolean,
    path?: boolean,
    academy?: boolean,
    notice?: boolean,
    poster?: boolean
}

class User {
    private static instance: User;

    public currentUser: null | {email: string | null; ido?:string; uid?:string} = null;

    // Auth
    public email: string = "";
    public uid: string = "";

    // DB
    public admin: boolean = false;
    public bizdev: boolean = false;
    public teamLeader: boolean = false;
    public manager: boolean = false;
    public root: boolean = false;
    public avatar: string = "base";
    public code: Code | undefined = undefined;
    public favorite_books: string[] = [];
    public favorite_activities: string[] = [];
    public onboardingOptIn: boolean | undefined = false;
    public optIn: boolean | undefined = false;
    public sub: Subscription | undefined = undefined;
    public version: string = version;
    public build: number = parseInt(build);
    public creation_version: string = "1.X";
    public creation_build: number = 347;
    public firstname: string = "";
    public lastname: string = "";
    public filter_by_level: string[] = ["cycle1", "cycle2", "cycle3"];
    public profile?: UserType;
    public steps?: Steps;

    // GAMIFICATION
    public level: number = 1;
    public pts: number = 0;
    public badges: any = {};
    public days_in_a_row: number = 1;

    // Niveau des enfants de l'utilisateur
    public children_level: "cycle1" | "cycle2" | "cycle3" | undefined = "cycle1";

    // Activités déjà réalisées dans le cadre des neurosciences
    public act_completed: string[] = [];

    // TNE
    public tne_ido?: string = "";
    public tne_pro?: string = "";
    public tne_p_ms2?: string = "";
    public tne_uai?: string = "";
    public demo?: boolean | undefined = undefined;

    // MEDIA - Liste des media vus pour griser ou non les listes bien-être, philo, etc...
    // Pour chaque catégorie, on va lister ce qui a éyté fait ou non
    public media?: { [key: string]: string[] } | undefined = undefined;

    private constructor() {

    }

    public static getInstance(): User {
        if (!User.instance) {
            User.instance = new User();
        }

        return User.instance;
    }

    public getUserData = () => {
        return this;
    }

    public removePreSub = () => {
        const todayInTS = new Date().getTime();
        this.sub = new Subscription({
            customer_email: this.email,
            creationDate: todayInTS,
            date: todayInTS,
            start_date: todayInTS,
            expires_date: todayInTS,
            pre_expires_date: todayInTS,
            auto_renewing: false,
            origin: "stripe",
            product_id: "stripe_cancel"
        });
    }

    public successPreSub = () => {
        this.sub.product_id = "stripe_sucess";
    }

    private isSupportedProfile = (profile: unknown): profile is UserType => {
        return profile === "neutral"
            || profile === "teacher"
            || profile === "family"
            || profile === "animator"
            || profile === "doctor";
    }

    private resetProfileData = () => {
        this.admin = false;
        this.bizdev = false;
        this.teamLeader = false;
        this.manager = false;
        this.root = false;
        this.avatar = "base";
        this.code = undefined;
        this.favorite_books = [];
        this.favorite_activities = [];
        this.onboardingOptIn = false;
        this.optIn = false;
        this.sub = undefined;
        this.firstname = "";
        this.lastname = "";
        this.filter_by_level = ["cycle1", "cycle2", "cycle3"];
        this.profile = undefined;
        this.steps = undefined;
        this.level = 1;
        this.pts = 0;
        this.badges = {};
        this.days_in_a_row = 1;
        this.children_level = "cycle1";
        this.act_completed = [];
        this.tne_ido = "";
        this.tne_pro = "";
        this.tne_p_ms2 = "";
        this.tne_uai = "";
        this.demo = undefined;
        this.media = undefined;
    }

    /**
     * Restore a complete cached profile for the currently authenticated account.
     * Reading the cache must happen before applying auth-only data, otherwise the
     * latter could replace the complete cached profile with only an email and UID.
     */
    public restoreCachedUserData = async (userAuthData: { email?: string | null; uid?: string }): Promise<boolean> => {
        if (!userAuthData.uid) return false;

        const storedUserData = await getItemFromAsyncStorage({ itemKey: AsyncStorageKey.userData });
        if (storedUserData === null) return false;

        const data: Partial<User> = storedUserData.data;
        if (data.uid !== userAuthData.uid || !this.isSupportedProfile(data.profile)) {
            return false;
        }

        this.uid = data.uid;
        this.email = data.email ?? userAuthData.email ?? "";
        this.admin = data.admin ?? false;
        this.bizdev = data.bizdev ?? false;
        this.teamLeader = data.teamLeader ?? false;
        this.manager = data.manager ?? false;
        this.root = data.root ?? false;
        this.avatar = data.avatar ?? "base";
        this.code = data.code !== undefined ? new Code(data.code) : undefined;
        this.favorite_books = data.favorite_books ?? [];
        this.favorite_activities = data.favorite_activities ?? [];
        this.onboardingOptIn = data.onboardingOptIn ?? false;
        this.optIn = data.optIn ?? false;
        this.sub = data.sub !== undefined ? new Subscription(data.sub) : undefined;
        this.version = data.version ?? version;
        this.build = data.build ?? parseInt(build);
        this.creation_version = data.creation_version ?? "1.X";
        this.creation_build = data.creation_build ?? 347;
        this.firstname = data.firstname ?? "";
        this.lastname = data.lastname ?? "";
        this.filter_by_level = data.filter_by_level ?? ["cycle1", "cycle2", "cycle3"];
        this.profile = data.profile;
        this.steps = data.steps ?? {
            filter: false,
            tutorial: false,
            webinar: false,
            advicesByLevel: false,
            advicesByActivityType: false,
            path: false,
            academy: false,
            notice: false,
            poster: false
        };
        this.level = data.level ?? 1;
        this.pts = data.pts ?? 0;
        this.badges = data.badges ?? {};
        this.days_in_a_row = data.days_in_a_row ?? 1;
        this.children_level = data.children_level;
        this.act_completed = data.act_completed ?? [];
        this.tne_ido = data.tne_ido ?? "";
        this.tne_pro = data.tne_pro ?? "";
        this.tne_p_ms2 = data.tne_p_ms2 ?? "";
        this.tne_uai = data.tne_uai ?? "";
        this.demo = data.demo;
        this.media = data.media;

        if (getAppTarget() !== "public") {
            this.profile = "teacher";
        }

        return true;
    }

    public initUserData = async (userAuthData: any) => {
        const didRestoreCachedProfile = await this.restoreCachedUserData(userAuthData);
        if (!didRestoreCachedProfile) {
            this.uid = userAuthData.uid;
            this.email = userAuthData.email;
        }
        return this;
    }

    public getUserProfileForAnalytics = () => {
        if (this.profile === "family") {
            return "parent";
        } else if (this.profile !== "neutral") {
            return this.profile;
        } else {
            return undefined;
        }
    }

    public getUserSubscriptionStatusForAnalytics = () => {
        let subDetails: { subStatus: SubStatus, endDate: string, code?: string } = {
            subStatus: "free",
            endDate: "free"
        }
        if (this.sub !== undefined) {
            const now = new Date().getTime();
            let startDate = this.sub.start_date !== undefined ? this.sub.start_date : this.sub.date !== undefined ? this.sub.date : 0;
            if ((now < this.sub.expires_date) && (now > startDate)) {
                const timeToEndDate: number = this.sub.expires_date - startDate;
                if (timeToEndDate < 864000000) {
                    subDetails = {
                        subStatus: "premium_trial",
                        endDate: getNumericDateForAnalyticForTimestamp(this.sub.expires_date)
                    }
                } else {
                    subDetails = {
                        subStatus: "premium",
                        endDate: getNumericDateForAnalyticForTimestamp(this.sub.expires_date)
                    }
                }
            }
            if ((this.sub.card_id !== undefined) && (this.sub.card_id.length > 0)) {
                // On va regarder s'il a un prefix
                if (this.sub.card_id.indexOf("_") !== -1) {
                    // Oui il y a un prefix
                    const codeSplitArray: string[] = this.sub.card_id.split("_");
                    subDetails.code = codeSplitArray[0];
                }
            }
        }
        return subDetails;
    }

    public isTeacher = () => {
        if ((this.profile === undefined) || ((this.profile !== undefined) && (this.profile === "teacher"))) {
            return true;
        };
        return false;
    }

    public userProfile = () => {
        return this.profile;
    }

    public getPremiumStatus = () => {
        let subStatus: SubStatus = "free";
        if (this.sub !== undefined) {
            const now = new Date().getTime();
            let startDate = this.sub.start_date !== undefined ? this.sub.start_date : this.sub.date !== undefined ? this.sub.date : 0;
            if ((now < this.sub.expires_date) && (now > startDate)) {
                subStatus = "premium";
            } else if ((this.sub.pre_expires_date !== undefined) && (this.sub.pre_expires_date > 0)) {
                // On va regarder si cela fait plus de 2h qu'il a demandé un abonnement et que le product_id est égale à stripe_success
                if ((this.sub.product_id === "stripe_success") && (now < this.sub.pre_expires_date) && (now > startDate) && (now - startDate < 7200000)) {
                    subStatus = "pre-sub";
                } else {
                    subStatus = "free";
                }
            }
        }
        console.log({subStatus})
        /*
        if (this.email.includes("axel") && this.email.includes("@lili.cool")) {
            return "premium";
        }
            */
        return subStatus;
    }

    public isPremium = () => {
        if (this.sub !== undefined) {
            const now = new Date().getTime();
            let startDate = this.sub.start_date !== undefined ? this.sub.start_date : this.sub.date !== undefined ? this.sub.date : 0;
            if ((now < this.sub.expires_date) && (now > startDate)) {
                return true;
            }
        };
        return false;
    }

    public isActivityFavorite(activityId: string) {
        if (this.favorite_activities !== undefined) {
            if (this.favorite_activities.indexOf(activityId) !== -1) {
                return true;
            }
        }
        return false;
    }

    public setAuthUserData = async (data: { email: string | null | undefined, uid: string | undefined }) => {
        const nextUid = data.uid ?? "";
        if (this.uid.length > 0 && this.uid !== nextUid) {
            this.resetProfileData();
        }
        this.email = data.email ?? "";
        this.uid = nextUid;
        return this;
    }

    public setDBUserData = async (data: { act_completed?: string[]; tne_ido?:string; manager?: boolean, root?: boolean, bizdev?: boolean, admin?: boolean, teamLeader?: boolean, firstname?: string, lastname?: string, avatar?: string, email: string, uid?: string, favorite_books?: string[], favorite_activities?: string[], onboardingOptIn?: boolean, optIn?: boolean, sub?: Subscription, code?: Code, creation_build?: number, build?: number, creation_version?: string, version?: string, filter_by_level?: string[], media?: { [key: string]: string[] }, profile?: UserProfile, steps?: Steps, children_level?: string }) => {
        if (data.admin !== undefined) {
            this.admin = data.admin;
        } else {
            this.admin = false;
        }
        if (data.act_completed !== undefined) {
            this.act_completed = data.act_completed;
        } else {
            this.act_completed = [];
        }
        if (data.bizdev !== undefined) {
            this.bizdev = data.bizdev;
        } else {
            this.bizdev = false;
        }
        if (data.teamLeader !== undefined) {
            this.teamLeader = data.teamLeader;
        } else {
            this.teamLeader = false;
        }
        if (data.root !== undefined) {
            this.root = data.root;
        } else {
            this.root = false;
        }
        if (data.manager !== undefined) {
            this.manager = data.manager;
        } else {
            this.manager = false;
        }
        this.avatar = data.avatar ?? "";
        this.firstname = data.firstname ?? "";
        this.lastname = data.lastname ?? "";
        this.email = data.email;
        this.favorite_books = data.favorite_books ?? [];
        this.favorite_activities = data.favorite_activities ?? [];
        this.creation_build = data.creation_build ?? 347;
        this.creation_version = data.creation_version ?? "1.X";
        this.build = data.build ?? parseInt(build);
        this.version = data.version ?? version;
        if (data.filter_by_level !== undefined) {
            this.filter_by_level = data.filter_by_level;
        }
        if (data.onboardingOptIn !== undefined) {
            this.onboardingOptIn = data.onboardingOptIn;
        }
        if (data.optIn !== undefined) {
            this.optIn = data.optIn;
        }
        this.sub = data.sub !== undefined ? new Subscription(data.sub) : undefined;
        if (data.code !== undefined) {
            this.code = data.code;
        } else {
            this.code = undefined;
        }
        if (data.profile !== undefined) {
            this.profile = data.profile;
        } else if (data.code !== undefined) {
            this.profile = "teacher";
        } else {
            this.profile = "family";
        }
        const appTarget: AppTarget = getAppTarget();
        if (appTarget !== "public") {
            this.profile = "teacher";
        }

        if (data.media !== undefined) {
            this.media = data.media;
        }
        if (data.steps !== undefined) {
            this.steps = data.steps;
        } else {
            this.steps = {
                filter: false,
                tutorial: false,
                webinar: false,
                advicesByLevel: false,
                advicesByActivityType: false,
                path: false,
                academy: false,
                notice: false,
                poster: false
            }
        }
        this.children_level = data.children_level;
        this.tne_ido = data.tne_ido ?? "";
        await this.saveUserData();
        return this;
    }

    public setActionsUserData = async (data: { level: number, pts: number, badges: any, days_in_a_row: number }) => {
        const { level, pts, badges, days_in_a_row } = data;
        this.level = level;
        this.pts = pts;
        this.badges = badges;
        this.days_in_a_row = days_in_a_row;
        await this.saveUserData();
        return this;
    }

    public revokeRenwability = async () => {
        let userSub = this.sub;
        userSub.auto_renewing = false;
        this.sub = userSub;
        this.saveUserData();
    }

    public addSubToUser = async (purchaserInfo) => {
        this.sub = new Subscription(purchaserInfo);
        addPremiumTagToUser({ email: this.email });
        await addDataToUser({
            key: "sub",
            value: purchaserInfo,
            user_id: this.uid
        });
    }

    saveUserData = async () => {
        if (this.uid.length === 0 || !this.isSupportedProfile(this.profile)) {
            return;
        }
        await setItemToAsyncStorage({itemKey: AsyncStorageKey.userData, item:this });
    }

    public deleteUser = async () => {
        this.currentUser = null;
        this.uid = "";
        this.lastname = "";
        this.firstname = "";
        this.tne_ido = "";
        this.tne_pro = "";
        this.tne_p_ms2 = "";
        this.tne_uai = "";
        this.admin = false;
        this.bizdev = false;
        this.teamLeader = false;
        this.avatar = "base";
        this.email = "";
        this.act_completed = [];
        this.profile = undefined;
        this.favorite_activities = [];
        this.favorite_books = [];
        this.onboardingOptIn = false;
        this.optIn = false;
        this.sub = undefined;
        this.code = undefined;
        this.level = 0;
        this.pts = 0;
        this.badges = {};
        this.days_in_a_row = 0;
        this.media = undefined;
        this.steps = undefined;
        await removeItemFromAsyncStorage({key:AsyncStorageKey.userData});
    }
}

export default User;
